import Service, { service } from '@ember/service';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type FeaturesService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type { PhoneCaptureSurface } from 'tio-data/data/resources/company-setting.type';
import { ALL_REGIONS, DEFAULT_COUNTRY } from 'tio-ui/components/phone-input';
import type { PhoneCountryCode } from 'tio-ui/components/phone-input';

/** Every capability this source answers. Anything else falls through untouched. */
const KEY_PREFIX = 'phoneCapture';

/**
 * Resolves the RFC 110 phone-capture capabilities from two independent gates, both of
 * which must be on before any surface appears:
 *
 *   environment — `features.isEnabled('phoneCapture')`: the `ENV.featureFlags` value baked
 *                 into the build, with the server's runtime flag merged over it when the
 *                 flag document is marked `exposeToWeb`. Go-live is flipping `production`
 *                 on that document, not rebuilding — the baked value is the fallback for
 *                 when tio-api cannot be reached.
 *   company     — the API's already-resolved `phoneCapture` payload, per partner.
 *
 * Registered as a capability source rather than read directly by templates so a surface
 * asks one question (`features.can('phoneCapture.surfaces.profile')`) instead of
 * repeating both gates and getting one of them wrong.
 *
 * Keys: `phoneCapture` (master), `phoneCapture.surfaces.<surface>`, and
 * `phoneCapture.postLoginOverlay.mobileOnly`.
 */
export default class PhoneCaptureService extends Service {
  @service declare features: FeaturesService;
  @service declare partner: PartnerService;

  /** The environment half: baked build value, overridden by the runtime flag when sent. */
  get isAvailable(): boolean {
    try {
      return this.features.isEnabled(KEY_PREFIX);
    } catch {
      // isEnabled throws when ENV is unseeded; capabilities gate boot-time templates.
      return false;
    }
  }

  /** Whether the partner has switched the feature on at all. */
  get isEnabledForCompany(): boolean {
    return this.isAvailable && this.partner.phoneCapture?.isEnabled === true;
  }

  /**
   * @param surface one of the five RFC 110 surfaces
   * @return false unless the environment flag, the company switch, and that surface
   *   are all on
   */
  isSurfaceEnabled(surface: PhoneCaptureSurface): boolean {
    return this.isEnabledForCompany && this.partner.phoneCapture?.surfaces?.[surface] === true;
  }

  /**
   * Regions a member may capture a number in, as the API resolved them (platform flags
   * ∩ company row). The NANP is always one; an older API that sends none means NANP only.
   */
  get regions(): PhoneCountryCode[] {
    const served = (this.partner.phoneCapture?.regions ?? []).filter(
      (code): code is PhoneCountryCode => (ALL_REGIONS as readonly string[]).includes(code)
    );

    return served.length ? served : [DEFAULT_COUNTRY];
  }

  /** Rollout lever: restrict the post-login overlay to mobile viewports. */
  get overlayIsMobileOnly(): boolean {
    return (
      this.isEnabledForCompany && this.partner.phoneCapture?.postLoginOverlay?.mobileOnly === true
    );
  }

  /**
   * Abstains (`undefined`) on every key it does not own, so unrelated capabilities keep
   * falling through to the flag and default tiers.
   */
  asCapabilitySource(): CapabilitySource {
    return {
      id: 'phone-capture',
      rank: CAPABILITY_SOURCE_RANK.entitlement,
      resolve: (key) => {
        const name = key as string;

        if (name !== KEY_PREFIX && !name.startsWith(`${KEY_PREFIX}.`)) {
          return undefined;
        }

        if (name === KEY_PREFIX) {
          return this.isEnabledForCompany;
        }

        if (name === `${KEY_PREFIX}.postLoginOverlay.mobileOnly`) {
          return this.overlayIsMobileOnly;
        }

        const surface = name.startsWith(`${KEY_PREFIX}.surfaces.`)
          ? (name.slice(`${KEY_PREFIX}.surfaces.`.length) as PhoneCaptureSurface)
          : null;

        // An unknown phoneCapture.* key is answered false rather than abstaining: a
        // typo must not fall through to the flag tier and read as enabled.
        return surface ? this.isSurfaceEnabled(surface) : false;
      },
    };
  }
}
